import { AccountInfo, InteractionStatus } from "@azure/msal-browser";
import { useAccount, useMsal } from "@azure/msal-react";
import { useEffect, useState } from "react";
import { clearStaleAuthStorage, createDynamicFetcher } from "../api/auth";
import { Footer } from "./Footer";
import { Navbar } from "./Navbar";
import { Client, Student, User, StaticData } from "../api/client";
import { GetBaseUrl } from "../api/build";
import { Outlet } from "react-router-dom";
import { SkeletonLoader } from "./NotAuthorized/SkeletonLoader";

// Define the authentication context state
export declare interface AuthContextState {
  client: Client | null; // Represents the client object for API interactions
  user: User | null; // Represents the user object with authentication information
  state?: boolean
}

// Function to create a null authentication context state
export function nullAuthContext(): AuthContextState {
  return {
    client: new Client(GetBaseUrl()), // Create a new client instance with the base URL
    user: null                        // Set the user property to null initially
  };
}

// Define the shape of the authentication context
export declare interface AuthContext {
  account: AccountInfo | null;                        // Represents the user account information
  loggedIn: boolean;                                  // Indicates whether the user is logged in
  login: () => void;                                  // Function to initiate the login process
  logout: () => void;                                 // Function to initiate the logout process
  state: AuthContextState;                            // Represents the authentication context state
  client: Client | null;                              // Represents the client object for API interactions
  student: Student | undefined;                       // Represents the student information or undefined if not available
  setStudent: React.Dispatch<Student> | undefined;    // React dispatch function to set the student information
  staticData: StaticData | null;                      // Adding static data                      
}

export enum LoadingState {
  LoggedOut = 0,                // Default State, we are logged out and do not have an Msal Account
  BeganLoginProcess = 1,        // MSAL account has been loaded and we are loading all the User, Static, and Admin data
  FinishedLoginProcess = 2,     // All necessary (user) data is loaded, and the front-end can now end its loading state
}

/*
  AuthWrapper component is responsible for managing the authentication state of the application. 
  It wraps the main content of the application, ensuring that the user's authentication status
  and related information are appropriately handled throughout the application's lifecycle.
*/
export function AuthWrapper(): JSX.Element {
  // Instance provides us with methods from MSAL, and inProgress shows the status of what MSAL is doing
  const { instance, inProgress } = useMsal();

  // Actual Microsoft account
  const account = useAccount();

  // InteractionStatus.None is our default state for inProgress when Msal finishes initializing
  const msalReady = inProgress === InteractionStatus.None;
  // Has user and backend Client
  const [state, setState] = useState<AuthContextState>(nullAuthContext());

  /// Stores student entity
  const [student, setStudent] = useState(state.user?.student);

  // Shows  that the user is logged in: got account from Microsoft AND loaded user
  // const [loggedIn, setLoggedIn] = useState<boolean>(false);
  const loggedIn = account !== null

  // Tracks where we are in the loading process in order to show the correct loading text
  const [loadingState, setLoadingState] = useState<LoadingState>(LoadingState.LoggedOut);

  // Stores static data
  const [staticData, setStaticData] = useState<StaticData | null>(null); // Add state for StaticData

  // Logic guard to see if we got the client and can use it
  const [isAuthenticatedClientReady, setIsAuthenticatedClientReady] = useState<boolean>(false);

  // On MSAL instance or account change 
  useEffect(() => {
    if (account) {
      // Replace client with authenticated one
      const dynamicFetcher = createDynamicFetcher(instance, account);
      const secureClient = new Client(GetBaseUrl(), dynamicFetcher as any);

      setState({ client: secureClient, user: null});
      setIsAuthenticatedClientReady(true);
    } 
  }, [account, instance]);

  // On state change, try to update current user's info
  useEffect(() => {
    // if using global context, return
    if (!isAuthenticatedClientReady) return;
    
    setLoadingState(LoadingState.BeganLoginProcess);
    // Get or create a new user and update the state
    (async () => {
      let user = await state.client?.user_GetCurrentUser();
      if (user === null) {
        user = await state.client?.user_CreateUser()
      }
      window.netID = user?.netID;

      //  Update student or faculty info 
      if (user?.student) {
        setStudent(user?.student);
      }

      // Could eventually change this to use research portal's method of checking version to avoid sending large amounts of data every time, but this is fine for now honestly

      const fetchedStaticData = await state.client?.user_GetStaticData(); // Assuming the method exists on the client
      if (fetchedStaticData) {
        setStaticData(fetchedStaticData);
      }

      setState({ ...state, user: user ?? null});
      setLoadingState(LoadingState.FinishedLoginProcess)
    })().catch((err) => {
      console.log(err);
      });
  }, [state.client])


  const login = async () => {
    if (loggedIn) return;
      // line that fixes infinite loop during sign in
      clearStaleAuthStorage();
  
      instance.loginRedirect({
        scopes: ["openid", "profile", "email"]
      });
  
      console.log("login redirect");
  }

  const logout = async () => {
    instance.logoutRedirect({
      account: account,
      logoutHint: account?.username,
    });


    // setLoggedIn(false);
    setState(nullAuthContext()) // reset state
    setStudent(undefined); // reset student
    setIsAuthenticatedClientReady(false);
  }

  if (inProgress == InteractionStatus.Login) {
    return <SkeletonLoader text="Redirecting"/>
  }
  
  if (!msalReady) {
    return <SkeletonLoader text="Initializing..."/>
  }
  
  if (loadingState == LoadingState.BeganLoginProcess && !state.user) {
    return <SkeletonLoader text="Loading..."/>
  }
  
  // Create an object representing the authentication context for the Outlet component
  const outletContext: AuthContext = {
    account: account, // Authenticated user's account information
    loggedIn: loggedIn, // Boolean indicating whether the user is logged in
    login: login, // Function to initiate the login process
    logout: logout, // Function to initiate the logout process
    state: state, // Current authentication state
    client: state.client, // API client used for making authenticated requests
    student: student, // Information about the authenticated student (if applicable)
    setStudent: setStudent, // Function to update the student state
    staticData: staticData,
  }
  
  // Render the AuthWrapper component along with child components
  return (
    <>
      <Navbar loggedIn={loggedIn} login={login} logout={logout} state={state} student={student} />
      <Outlet context={outletContext} />
      <Footer />
    </>
  )
}
